/* eslint-disable no-shadow */
import * as React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useState } from 'react';
import { Widget } from '../../../pages/UserGroupsPage';
import WidgetsBox from '../../widgetsBox';
import api from '../../../services/api';
import { DownloadItem } from '../../addUrlDownloads';
import { useAuth } from '../../../context/authContext';

interface props {
  setOpen: any;
  widgetsListForm: Widget[];
  handleCloseOptions?: any;
  handleAdd?: any;
  getUserGroups?: any;
  handleSaveEdit?: any;
  handleSaveSettings?: any;
  usergroup?: any;
  handleClose: any;
}
const SettingsUserGroupContent: React.FC<props> = ({
  setOpen,
  handleCloseOptions = undefined,
  handleClose,
  usergroup = undefined,
}) => {
  const { refreshUserGroups } = useAuth();
  const [userGroupNameForm, setUserGroupNameForm] = useState<string>('');

  const [widgetsTree, setWidgetsTree] = useState<Widget>();
  const [widgetsToDelete, setWidgetsToDelete] = useState<Widget[]>([]);

  const [fileIdsToDelete, setFileIdsToDelete] = useState<number[]>([]);

  const uploadFile = async (
    usergroupId: number,
    file: File,
    description: string,
  ): Promise<DownloadItem | any> => {
    // console.log('Uploadeding file...', file);
    try {
      // setLoadingMessage(`Uploading file '${file.name}'`);

      const uploadConfig = {
        headers: {
          'content-type': 'multipart/form-data',
        },
      };

      const data = new FormData();
      data.append('file', file);
      data.append('description', description);

      const response = await api.post(
        `/files/${usergroupId}`,
        data,
        uploadConfig,
      );

      const urlString = response.request?.responseURL.replace(
        '/files',
        '/uploads',
      );
      const lastSlashIndex = urlString.lastIndexOf('/');
      const newUrlString = urlString.substring(0, lastSlashIndex);

      return {
        fileId: response.data.fileId,
        description,
        url: `${newUrlString}/${response.data.filename ?? ''}`,
      };
    } catch (error: any) {
      console.error(error.response.data.message);
      return {};
    }
  };

  const updateWidgetDownloadItems = useCallback(
    async (widgetTree: Widget): Promise<Widget> => {
      // Update the current widget's downloadItems
      const newDownloadItens = widgetTree.downloadItens
        ? await Promise.all(
            widgetTree.downloadItens.map(async (item: DownloadItem) => {
              if (!item.fileToBeUploaded) {
                return item; // Return item unchanged if no file to be uploaded
              }

              // Upload the file and return the updated item
              return uploadFile(
                usergroup.userGroupId,
                item.fileToBeUploaded,
                item.description,
              );
            }),
          )
        : [];

      // Recursively process the children (items) of the widgetNode
      const updatedWidgetNode = {
        ...widgetTree,
        downloadItens: newDownloadItens,
        children: widgetTree.children
          ? await Promise.all(
              widgetTree.children.map(childNode =>
                updateWidgetDownloadItems(childNode),
              ),
            )
          : undefined, // Keep items as undefined if no children
      };

      return updatedWidgetNode;
    },
    [usergroup.userGroupId],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Delete files that were uploaded but not saved
      if (fileIdsToDelete.length > 0) {
        try {
          const queryString = fileIdsToDelete
            .map(id => `fileIdsToDelete=${id}`)
            .join('&');
          api.delete(`/files/deleteByIds?${queryString}`);
        } catch (error: any) {
          console.error('Error deleting files', error.response.data.message);
        }
      }

      if (widgetsTree) {
        const updatedWidgetsTree = await updateWidgetDownloadItems(widgetsTree);

        api
          .post('/widgets/updateTree', {
            tree: updatedWidgetsTree,
            widgetsToDelete,
          })
          .then((res: any) => {
            console.log(res);
            // refresh the logged-in user's session so widget changes
            // (e.g. spinrad values) take effect right away, no re-login needed
            refreshUserGroups({ force: true });
          })
          .catch((err: any) => {
            console.log(err);
          });
      }

      setOpen(false);
      handleCloseOptions();
    },
    [
      fileIdsToDelete,
      handleCloseOptions,
      refreshUserGroups,
      setOpen,
      updateWidgetDownloadItems,
      widgetsToDelete,
      widgetsTree,
    ],
  );

  const handleCancel = useCallback((): void => {
    setUserGroupNameForm('');
    setOpen(false);
    if (usergroup) {
      handleCloseOptions();
    }
    handleClose();
  }, [handleClose, handleCloseOptions, setOpen, usergroup]);

  useEffect(() => {
    if (usergroup) {
      setUserGroupNameForm(usergroup.name);

      api
        .get(`/widgets/tree/${usergroup.settings.widgets}`)
        .then((response: any) => {
          setWidgetsTree(response.data);
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [usergroup]);

  const addFileIdsToDeleteList = (fileIds: number[]): void => {
    // Files to be deleted on save
    setFileIdsToDelete([...fileIdsToDelete, ...fileIds]);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        style={{ backgroundColor: '#c2c2c2', width: '50vw' }}
      >
        <DialogTitle>{`User Group Settings: ${userGroupNameForm}`}</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              gap: '2%',
            }}
          >
            <TextField
              margin="dense"
              id="userGroupName"
              name="userGroupName"
              value={userGroupNameForm}
              onChange={event => setUserGroupNameForm(event.target.value)}
              label="User Group Name"
              type="text"
              variant="outlined"
              disabled={true}
              style={{ marginBottom: '20px', width: '100%' }}
              inputProps={{ maxLength: 50 }}
            />
          </div>
          {widgetsTree && (
            <WidgetsBox
              widgetsTree={widgetsTree}
              setWidgetsTree={setWidgetsTree}
              setWidgetsToDelete={setWidgetsToDelete}
              usergroupId={usergroup.userGroupId} // TODO: PREENCHER ISSO COM O USERGROUP REAL
              addFileIdsToDeleteList={addFileIdsToDeleteList}
            ></WidgetsBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="submit">{usergroup ? 'Save All' : 'Add'}</Button>
        </DialogActions>
      </Box>
    </>
  );
};

export default SettingsUserGroupContent;
